import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AvoirService, Avoir, AvoirLine } from '../service/avoir.service';
import { FinancialReportService } from '../service/financial-report.service';
import { Order } from '../Module/Order.model';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-return-order',
  templateUrl: './return-order.component.html',
  styleUrls: ['./return-order.component.css']
})
export class ReturnOrderComponent implements OnInit {
  returnForm: FormGroup;
  order: Order;
  isSubmitting = false;
  currentCash = 0;
  cashReady = false;

  // Custom Confirmation Dialog State
  isConfirmVisible = false;
  confirmTitle = '';
  confirmMessage = '';
  confirmType: 'info' | 'warning' | 'danger' = 'info';
  confirmAction: (() => void) | null = null;
  confirmBtnText = 'D\'accord';

  openConfirm(title: string, message: string, type: 'info' | 'warning' | 'danger', action: () => void = () => {}, btnText: string = 'D\'accord'): void {
    this.confirmTitle = title;
    this.confirmMessage = message;
    this.confirmType = type;
    this.confirmAction = action;
    this.confirmBtnText = btnText;
    this.isConfirmVisible = true;
  }

  handleConfirm(): void {
    if (this.confirmAction) {
      this.confirmAction();
    }
    this.isConfirmVisible = false;
  }

  handleCancel(): void {
    this.isConfirmVisible = false;
    this.confirmAction = null;
  }

  constructor(
    private fb: FormBuilder,
    private avoirService: AvoirService,
    private reportService: FinancialReportService,
    private router: Router,
    public dialogRef: MatDialogRef<ReturnOrderComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { order: Order, avoir?: Avoir }
  ) {
    this.order = data.order;
    this.avoir = data.avoir;
    this.isEditMode = !!data.avoir;
    this.returnForm = this.fb.group({
      notes: [this.avoir?.notes || ''],
      discountAdjustment: [this.avoir?.discountAdjustment || 0, [Validators.min(0)]],
      lines: this.fb.array([])
    });
  }

  isEditMode = false;
  avoir?: Avoir;

  ngOnInit(): void {
    if (this.order && this.order.orderLines) {
      this.order.orderLines.forEach(line => {
        const product = line.product;
        // Total returned quantity from ALL avoirs (from backend)
        const totalReturned = line.returnedQuantity || 0;
        
        // Quantity in the Avoir we are currently editing (if any)
        let currentAvoirQty = 0;
        let isDefective = false;
        if (this.isEditMode && this.avoir?.avoirLines) {
          const matchingLine = this.avoir.avoirLines.find(al => al.product?.id === product?.id);
          if (matchingLine) {
            currentAvoirQty = matchingLine.quantityReturned || 0;
            isDefective = !!matchingLine.isDefective;
          }
        }

        // Max returnable = Total Order Qty - (Total Returned - Current Avoir Qty)
        const excludedReturned = totalReturned - currentAvoirQty;
        const maxReturnable = line.quantity - excludedReturned;

        const lineGroup = this.fb.group({
          product: [line.product],
          originalQuantity: [line.quantity],
          previouslyReturned: [excludedReturned], // This is the sum of OTHER returns
          maxReturnable: [maxReturnable],
          price: [line.realSellingPrice || line.product?.price],
          quantityReturned: [currentAvoirQty, [Validators.required, Validators.min(0), Validators.max(maxReturnable)]],
          isDefective: [isDefective]
        });

        // If they already returned everything (excluding what's in this edit), disable the input
        if (maxReturnable <= 0 && currentAvoirQty <= 0) {
          lineGroup.get('quantityReturned')?.disable();
        }

        this.lines.push(lineGroup);
      });
    }

    // Fetch current cash
    this.reportService.getCurrentCash().subscribe(cash => {
      this.currentCash = cash;
      this.cashReady = true;
      this.checkCashEnforcement();
    });

    // Watch for changes to enforce pending if cash is low
    this.lines.valueChanges.subscribe(() => {
      this.checkCashEnforcement();
    });
  }

  checkCashEnforcement(): void {
    if (!this.cashReady) return;
    // We visually enforce it in HTML via the bar color, 
    // no need to set a form control anymore since backend auto-approves.
  }

  isOrderPaid(order: any): boolean {
    if (!order) return false;
    return order.isPaid === true || order.isPaid?.toString() === 'true';
  }

  get lines(): FormArray {
    return this.returnForm.get('lines') as FormArray;
  }

  get totalRefundAmount(): number {
    let total = 0;
    this.lines.controls.forEach(control => {
      const qtyInfo = control.get('quantityReturned')?.value || 0;
      const priceInfo = control.get('price')?.value || 0;
      total += (qtyInfo * priceInfo);
    });
    return total;
  }

  get netRefundAmount(): number {
    const adjustment = this.returnForm.get('discountAdjustment')?.value || 0;
    return Math.max(0, this.totalRefundAmount - adjustment);
  }

  get orderDebt(): number {
    if (!this.order) return 0;
    
    // Explicitly check for the 'Paid' status first. 
    if (this.isOrderPaid(this.order)) {
      return 0;
    }

    // Use netTotal if returns have already been made, otherwise use initial total
    const baseTotal = this.order.hasReturns && this.order.netTotal !== undefined 
                      ? this.order.netTotal 
                      : (this.order.total ?? 0) - (this.order.discount ?? 0);
                      
    const paid = this.order.advancePayment ?? 0;
    return Math.max(0, baseTotal - paid);
  }

  get amountAbsorbedFromDebt(): number {
    return Math.min(this.netRefundAmount, this.orderDebt);
  }

  get finalCashRefund(): number {
    return Math.max(0, this.netRefundAmount - this.amountAbsorbedFromDebt);
  }

  get isCashSufficient(): boolean {
    if (this.finalCashRefund <= 0) return true;
    return this.currentCash >= this.finalCashRefund;
  }

  get hasAnyReturnable(): boolean {
    return this.lines.controls.some(control => (control.get('maxReturnable')?.value || 0) > 0);
  }

  get isAllSelected(): boolean {
    return this.lines.controls
      .filter(c => (c.get('maxReturnable')?.value || 0) > 0)
      .every(c => c.get('quantityReturned')?.value === c.get('maxReturnable')?.value);
  }

  selectAllReturnable(): void {
    this.lines.controls.forEach(control => {
      const max = control.get('maxReturnable')?.value || 0;
      if (max > 0) {
        control.get('quantityReturned')?.setValue(max);
      }
    });
    this.applyFullDiscountAdjustment();
  }

  clearAll(): void {
    this.lines.controls.forEach(control => {
      control.get('quantityReturned')?.setValue(0);
    });
    this.returnForm.get('discountAdjustment')?.setValue(0);
  }

  applyFullDiscountAdjustment(): void {
    if (this.order && this.order.discount) {
      this.returnForm.get('discountAdjustment')?.setValue(this.order.discount);
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  submitReturn(): void {
    if (this.returnForm.invalid || this.totalRefundAmount <= 0) {
      return;
    }

    this.isSubmitting = true;

    const avoirLines: AvoirLine[] = [];

    this.lines.controls.forEach(control => {
      const returnedQty = control.get('quantityReturned')?.value;
      if (returnedQty > 0) {
        avoirLines.push({
          product: control.get('product')?.value,
          quantityReturned: returnedQty,
          isDefective: control.get('isDefective')?.value
        });
      }
    });

    const isInsufficientCash = !this.isCashSufficient;

    const avoirSubmit: Avoir = {
      id: this.avoir?.id, // Keep the ID if editing
      originalOrderId: this.order.id!,
      avoirLines: avoirLines,
      notes: this.returnForm.get('notes')?.value,
      discountAdjustment: this.returnForm.get('discountAdjustment')?.value || 0,
      status: isInsufficientCash ? 'PENDING' : 'APPROVED',
      avoirDate: this.avoir?.avoirDate || new Date()
    };

    const request = this.isEditMode && this.avoir?.id
      ? this.avoirService.updateAvoir(this.avoir.id, avoirSubmit)
      : this.avoirService.createAvoir(avoirSubmit);

    request.subscribe({
      next: (res: Avoir) => {
        this.isSubmitting = false;
        
        // Success criteria: If it's already approved, or ready to be (meaning cash was found)
        const isActuallyReady = res.status === 'APPROVED' || res.status === 'READY_TO_APPROVE' || res.status === 'COMPLETED';
        const isPending = res.status === 'PENDING';
        
        if (isActuallyReady && !isPending) {
          // Immediate Success
          this.dialogRef.close(true);
          if (res.id) {
            this.router.navigate(['/avoir-detail', res.id]);
          }
        } else {
          // Show Status Popup
          const title = isPending ? 'Avoir en File d\'Attente' : 'Avoir Créé et Prêt';
          const message = isPending 
            ? 'La demande est en attente. Elle sera prête à être validée dès que la caisse disposera du cash.'
            : 'L\'avoir est prêt à être validé par un administrateur pour impacter le stock et la caisse.';

          this.openConfirm(
            this.isEditMode ? 'Avoir Mis à Jour' : title,
            message,
            isPending ? 'warning' : 'info',
            () => {
              this.dialogRef.close(true);
              if (res.id) {
                this.router.navigate(['/avoir-detail', res.id]);
              }
            },
            'Voir l\'Avoir'
          );
        }
      },
      error: (err) => {
        console.error('Error creating avoir:', err);
        this.isSubmitting = false;
        this.openConfirm(
          'Erreur',
          'Une erreur s\'est produite : ' + (err.error?.message || 'Inconnue'),
          'danger'
        );
      }
    });
  }
}
